import { useHandleAnchor } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/anchor/useHandleAnchor';
import { useGetApPayeeInfo } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/useGetApPayeeInfo';
import { DataMaster } from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { Anchor, Loading } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, Suspense, useEffect, useRef } from 'react';
import {
  AiAgentSettings,
  DefaultPaymentMethod,
  GeneralInformation,
  InvoiceReceivingMethod,
  PICAndContactInformation,
} from './components';
import { useAiAgentSettingsVisibility } from './components/AiAgentSettings/hooks/useAiAgentSettingsVisibility';
import { AnchorProvider } from './context/AnchorContext';
import { PayeeDetailDrawerProvider } from './context/PayeeDetailDrawerProvider';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);
type DetailDrawerContentProps = {
  handleUpdate?: () => void;
  data?: DataMaster | undefined;
  apPayeeId: string;
};

type Props = {
  apPayeeId: string;
  onClose?: (isCloseWithDirty?: boolean) => void;
  onEditingChange?: (isEditing: boolean) => void;
  onEditFormDirtyChange?: (isEditFormDirty: boolean) => void;
  onConfirmCancelEdit?: () => Promise<boolean>;
};

const DetailDrawerContent: FC<DetailDrawerContentProps> = ({
  data,
  apPayeeId,
}) => {
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const { isVisible: showAiAgentSettings } = useAiAgentSettingsVisibility({
    data: data?.aiAgentSettings,
  });

  const {
    handleAnchorClick,
    anchorItems,
    getContainer,
    getCurrentAnchor,
    resetAnchor,
    scrollToSection,
  } = useHandleAnchor(contentWrapperRef, showAiAgentSettings);

  useEffect(() => {
    if (apPayeeId) {
      resetAnchor();
    }
  }, [apPayeeId, resetAnchor]);

  return (
    <AnchorProvider scrollToSection={scrollToSection}>
      <div ref={contentWrapperRef} className={cx('detailDrawerBodyContainer')}>
        <Anchor
          getContainer={getContainer}
          getCurrentAnchor={getCurrentAnchor}
          items={anchorItems}
          affix={false}
          onClick={handleAnchorClick}
          className={cx('payeeAnchorContainer')}
          containerClassName={cx('anchorContainer')}
          contentClassName={cx('anchorContentContainer')}
        >
          <div className={cx('detailSectionContainer')}>
            <GeneralInformation data={data?.generalInformation} />
            <PICAndContactInformation
              data={data?.PICAndContractDetail}
              isEditable={data?.isEditable}
            />
            <InvoiceReceivingMethod
              data={data?.receivingMethod}
              isEditable={data?.isEditable}
            />
            {showAiAgentSettings && (
              <AiAgentSettings
                data={data?.aiAgentSettings}
                isEditable={data?.isEditable}
              />
            )}
            <DefaultPaymentMethod
              apPayeeId={apPayeeId}
              paymentRule={data?.paymentRule}
              transactionDefaultValue={data?.transactionDefaultValue}
              isEditable={data?.isEditable}
            />
          </div>
        </Anchor>
      </div>
    </AnchorProvider>
  );
};

DetailDrawerContent.displayName = 'DetailDrawerContent';

const FallBackLoading = () => {
  return (
    <div className={cx('loadingContainer')}>
      <Loading size='large' color='success' />
    </div>
  );
};

const InnerDetailDrawerContainer: FC<Props> = ({
  apPayeeId,
  onClose,
  onEditingChange,
  onEditFormDirtyChange,
  onConfirmCancelEdit,
}) => {
  const { data, refetch } = useGetApPayeeInfo(apPayeeId, onClose);

  if (!data) {
    return <FallBackLoading />;
  }

  return (
    <PayeeDetailDrawerProvider
      refetch={refetch}
      onClose={onClose}
      onEditingChange={onEditingChange}
      onEditFormDirtyChange={onEditFormDirtyChange}
      onConfirmCancelEdit={onConfirmCancelEdit}
    >
      <DetailDrawerContent data={data} apPayeeId={apPayeeId} />
    </PayeeDetailDrawerProvider>
  );
};

InnerDetailDrawerContainer.displayName = 'InnerDetailDrawerContainer';

export const DetailDrawerContainer: FC<Props> = (props) => {
  return (
    <Suspense fallback={<FallBackLoading />}>
      <InnerDetailDrawerContainer {...props} />
    </Suspense>
  );
};

DetailDrawerContainer.displayName = 'DetailDrawerContainer';
